// src/components/shared/AddFormLayout.js
// Shared visual shell for every full-page "Add ..." form (Connections,
// Transfer Commands, API Configs, etc). Redesigned from the old pattern of
// a bare <Paper><Typography variant="h6"/><TextField margin="dense"/><br/>
// ...</Paper> stack — one long unstructured list of fields with no visual
// hierarchy — into a card with:
//   - a small brand-accent tab + title/subtitle header (same language as
//     AppDialog's title bar)
//   - fields grouped into labeled sections instead of one long list
//   - a left section-nav rail to jump between sections (auto-hidden for
//     single-section forms — a rail with one item has no purpose)
//   - a footer with Back / Cancel / primary action
//
// Pair with `useSectionedForm` (drives which section is active + jumps to
// the first section with a validation error) and `SectionGrid` (2-column
// responsive layout for the fields inside a section). See
// connectionCommandForm.js (multi-section) and taskScheduleForm.js
// (single-section) for full worked examples.
//
// Most forms just pass `onBack` (defaults to navigate(-1)) — a single
// "← Back" link is enough when there's only one place to go back to. Forms
// nested two levels deep (e.g. a transfer command, which lives under one
// transfer, which lives under the root transfers list) should pass
// `breadcrumbs` instead: [{ label, onClick, mono? }, ...], with the last
// entry being the current page (no onClick — renders as plain muted text).
// `mono: true` on a crumb (e.g. an ID) gives it a monospace treatment so it
// reads as an identifier rather than a label. When `breadcrumbs` is set it
// replaces the BackLink/topExtra row entirely — see
// connectionCommandForm.js for a real example.
import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import { useTheme, alpha } from "@mui/material/styles";
import BackLink from "./BackLink";

const AddFormLayout = ({
  title,
  subtitle,
  badge,
  breadcrumbs,
  sections,
  activeSectionId,
  onSectionChange,
  onBack,
  primaryLabel,
  onPrimaryClick,
  primaryDisabled,
  secondaryLabel,
  onSecondaryClick,
  topExtra,
  children,
}) => {
  const theme = useTheme();
  const showNav = sections && sections.length > 1;
  const activeSection = sections?.find((s) => s.id === activeSectionId);

  return (
    <div className="form">
      {breadcrumbs ? (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, pl: "15px", mb: 0.5 }}>
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            return (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {i > 0 && (
                  <Box component="span" sx={{ color: "text.disabled", fontSize: "0.8125rem" }}>
                    /
                  </Box>
                )}
                {isLast ? (
                  <Typography
                    component="span"
                    sx={{ fontSize: "0.8125rem", color: "text.secondary", fontWeight: 500 }}
                  >
                    {crumb.label}
                  </Typography>
                ) : (
                  <Typography
                    component="span"
                    onClick={crumb.onClick}
                    sx={{
                      fontSize: crumb.mono ? "0.78rem" : "0.8125rem",
                      fontFamily: crumb.mono
                        ? "ui-monospace, SFMono-Regular, Menlo, monospace"
                        : "inherit",
                      color: "primary.main",
                      fontWeight: 600,
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {crumb.label}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <BackLink onClick={onBack} />
          {topExtra}
        </Box>
      )}

      <Box
        sx={{
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          boxShadow: "0 1px 6px rgba(15, 23, 42, 0.06)",
          maxWidth: 920,
          mx: "auto",
          mt: 1,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3.5,
            pt: 3,
            pb: 2.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 4,
                height: 20,
                borderRadius: 4,
                bgcolor: "primary.main",
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
              {title}
            </Typography>
            {badge && (
              <Chip
                label={badge}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: "primary.dark",
                  fontWeight: 600,
                  height: 22,
                }}
              />
            )}
          </Box>
          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, ml: 2.25 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex" }}>
          {showNav && (
            <Box
              sx={{
                width: 196,
                flexShrink: 0,
                borderRight: "1px solid",
                borderColor: "divider",
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              {sections.map((s, i) => {
                const active = s.id === activeSectionId;
                return (
                  <Box
                    key={s.id}
                    onClick={() => onSectionChange(s.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.25,
                      py: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                      fontSize: "0.8rem",
                      fontWeight: active ? 700 : 500,
                      color: active ? "primary.dark" : "text.secondary",
                      bgcolor: active
                        ? alpha(theme.palette.primary.main, 0.1)
                        : "transparent",
                      "&:hover": {
                        bgcolor: active
                          ? alpha(theme.palette.primary.main, 0.1)
                          : "action.hover",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        bgcolor: s.hasError
                          ? "error.main"
                          : active
                            ? "primary.main"
                            : "action.selected",
                        color: s.hasError || active
                          ? "#fff"
                          : "text.secondary",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {s.hasError ? "!" : i + 1}
                    </Box>
                    {s.label}
                  </Box>
                );
              })}
            </Box>
          )}

          <Box sx={{ flex: 1, p: 3.5, minWidth: 0 }}>
            {activeSection && (
              <Typography
                variant="subtitle2"
                sx={{ color: "text.secondary", mb: 2, fontWeight: 600 }}
              >
                {activeSection.label}
              </Typography>
            )}
            {children}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 3.5,
            py: 2.25,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "#FAFBFC",
          }}
        >
          {secondaryLabel && (
            <Button variant="text" color="inherit" onClick={onSecondaryClick}>
              {secondaryLabel}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={onPrimaryClick}
            disabled={primaryDisabled}
          >
            {primaryLabel}
          </Button>
        </Box>
      </Box>
    </div>
  );
};

export default AddFormLayout;
